'use client';

import React from 'react';
import { Typography } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faFolderBookmark } from '@fortawesome/pro-solid-svg-icons';

export interface CodeSetSelectorFolderProps {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Drag-and-drop handlers (Discovery / Manage). Optional. */
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  'data-testid'?: string;
}

/**
 * A collapsible folder used inside the code-set selector dropdowns. Keeps the
 * folder header (icon + label) and indented contents consistent across selectors.
 */
const CodeSetSelectorFolder: React.FC<CodeSetSelectorFolderProps> = ({
  label,
  isOpen,
  onToggle,
  children,
  onDragOver,
  onDragLeave,
  onDrop,
  'data-testid': testId,
}) => (
  <div
    className="w-full"
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    data-testid={testId}
  >
    <button
      type="button"
      className="flex items-center w-full px-4 py-2 hover:bg-neutral-100 cursor-pointer"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={`${label} folder, ${isOpen ? 'expanded' : 'collapsed'}`}
    >
      <div className="w-4 h-4 flex items-center justify-center mr-2">
        <Icon
          icon={isOpen ? faFolderOpen : faFolderBookmark}
          className="text-neutral-600"
          aria-hidden="true"
        />
      </div>
      <Typography className="text-sm font-semibold text-neutral-800 text-left">
        {label}
      </Typography>
    </button>

    {isOpen && <div className="ml-6">{children}</div>}
  </div>
);

export default CodeSetSelectorFolder;
